import React, { useEffect, useState, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import EventGalleryImageCard from '../components/EventGalleryImageCard';
import GalleryLightbox from '../components/GalleryLightbox';
import styles from './OrganizerEventGalleryAdminPage.module.css';
import { useTranslation } from 'react-i18next';

const OrganizerEventGalleryAdminPage = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const { t } = useTranslation('galleryAdmin');
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [actionLoading, setActionLoading] = useState(null); // image id
  const isGlobalMode = !id;
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const pendingImages = images.filter((img) => img.status !== 'approved');
  const approvedImages = images.filter((img) => img.status === 'approved');

  const fetchPending = useCallback(() => {
    setLoading(true);
    setError('');
    const endpoint = isGlobalMode
      ? `${process.env.REACT_APP_API_URL}/organizer/memories/pending`
      : `${process.env.REACT_APP_API_URL}/organizer/events/${id}/memories/pending`;

    axios.get(endpoint)
      .then(res => {
        const data = Array.isArray(res.data.data) ? res.data.data : (Array.isArray(res.data) ? res.data : []);
        setImages(data);
      })
        .catch(() => setError(t('errFetch')))
      .finally(() => setLoading(false));
  }, [id, isGlobalMode]);

  useEffect(() => { fetchPending(); }, [fetchPending]);

  const friendlyModerationError = (status, raw) => {
    if (status === 403) return t('err403');
    if (status === 404) return t('err404');
    if (status === 422) return raw || t('err422');
    if (status === 500) return t('err500');
    return raw || t('errDefault');
  };

  const executeAction = async (img, action) => {
    const eventPathId = img?.event_path_id || id || img?.event_id;
    if (!eventPathId) {
      alert(t('alertNoEventId'));
      return;
    }
    setActionLoading(img.id);
    try {
      const res = await axios.patch(
        `${process.env.REACT_APP_API_URL}/organizer/events/${eventPathId}/memories/${img.id}`,
        { action },
        undefined
      );
      if (action === 'reject' || action === 'delete') {
        setImages(prev => prev.filter(i => i.id !== img.id));
      } else {
        const updated = res?.data?.data ?? res?.data ?? null;
        if (updated?.id) {
          setImages(prev => prev.map((item) => (item.id === img.id ? { ...item, ...updated } : item)));
        } else {
          fetchPending();
        }
      }
    } catch (e) {
      const status = e?.response?.status;
      const raw = e?.response?.data?.message;
      alert(friendlyModerationError(status, raw));
    } finally {
      setActionLoading(null);
    }
  };

  const runAction = async (img, action) => {
    if (action === 'delete') {
      setDeleteTarget(img);
      return;
    }

    await executeAction(img, action);
  };

  const closeDeleteModal = () => {
    if (actionLoading && deleteTarget && actionLoading === deleteTarget.id) return;
    setDeleteTarget(null);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    await executeAction(deleteTarget, 'delete');
    setDeleteTarget(null);
  };

  const openLightbox = (index) => {
    if (!images.length) return;
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  const renderImageSection = (title, sectionImages, emptyMessage) => {
    if (sectionImages.length === 0) {
      return (
        <div className={styles.card}>
          <h2 className={styles.sectionHeading}>{title}</h2>
          <p className={styles.emptyMsg}>{emptyMessage}</p>
        </div>
      );
    }

    return (
      <section className={styles.imageSection}>
        <h2 className={styles.sectionHeading}>{title}</h2>
        <div className={styles.grid}>
          {sectionImages.map((img) => {
            const imageIndex = images.findIndex((item) => item.id === img.id);
            return (
              <div key={img.id} className={styles.cardWrapper}>
                {isGlobalMode && (
                  <div className={styles.eventLabelRow}>
                    <span className={styles.eventLabel}>{img.event_name || t('eventLabel', { id: img.event_id })}</span>
                    {img.event_path_id && (
                      <Link className={styles.eventLink} to={`/events/${img.event_path_id}/gallery-admin`}>
                        {t('eventLinkOnly')}
                      </Link>
                    )}
                  </div>
                )}
                <div className={styles.statusRow}>
                  <span className={`${styles.statusBadge} ${img.status === 'approved' ? styles.statusApproved : styles.statusPending}`}>
                    {img.status === 'approved' ? t('statusApproved') : t('statusPending')}
                  </span>
                </div>
                <EventGalleryImageCard image={img} onImageClick={() => openLightbox(imageIndex)} />
                <div className={styles.actions}>
                  <button
                    className={styles.approveBtn}
                    onClick={() => runAction(img, 'approve')}
                    disabled={actionLoading === img.id || img.status === 'approved'}
                  >{img.status === 'approved' ? t('approvedBtn') : t('approveBtn')}</button>
                  <button
                    className={styles.rejectBtn}
                    onClick={() => runAction(img, 'reject')}
                    disabled={actionLoading === img.id}
                  >{t('rejectBtn')}</button>
                </div>
              </div>
            );
          })}
        </div>
      </section>
    );
  };

  if (!user) return <p>{t('authRequired')}</p>;

  return (
    <div className={styles.page}>
      <div className={styles.container}>
        {/* ページヘッダー */}
        <div className={styles.pageHeader}>
          <h1 className={styles.heading}>
            {isGlobalMode ? t('headingGlobal') : t('heading')}
          </h1>
          <p className={styles.desc}>{t('desc')}</p>
        </div>

        {/* ツールバー */}
        <div className={styles.toolbar}>
          <button className={styles.reloadBtn} onClick={fetchPending} disabled={loading}>
            {t('reloadBtn')}
          </button>
          <span className={styles.count}>{t('countLabel', { pending: pendingImages.length, approved: approvedImages.length })}</span>
        </div>

        {/* 状態表示 */}
        {loading && <p className={styles.stateText}>{t('loading')}</p>}
        {error && <p className={styles.errorMsg}>{error}</p>}

        {/* 画像なし */}
        {!loading && !error && images.length === 0 && (
          <div className={styles.card}>
            <p className={styles.emptyMsg}>{t('emptyAll')}</p>
          </div>
        )}

        {images.length > 0 && (
          <>
            {renderImageSection(t('sectionPending'), pendingImages, t('emptyPending'))}
            {renderImageSection(t('sectionApproved'), approvedImages, t('emptyApproved'))}
          </>
        )}
      </div>

      <GalleryLightbox
        images={images}
        initialIndex={lightboxIndex}
        isOpen={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
      />

      {deleteTarget && (
        <div className={styles.modalBackdrop} onClick={closeDeleteModal}>
          <div className={styles.modalCard} onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label={t('modalAriaLabel')}>
            <h3 className={styles.modalTitle}>{t('modalTitle')}</h3>
            <p className={styles.modalText}>{t('modalDesc')}</p>
            <div className={styles.modalActions}>
              <button
                type="button"
                className={styles.confirmButton}
                onClick={confirmDelete}
                disabled={actionLoading === deleteTarget.id}
              >
                {actionLoading === deleteTarget.id ? t('deletingBtn') : t('confirmDeleteBtn')}
              </button>
              <button
                type="button"
                className={styles.cancelButton}
                onClick={closeDeleteModal}
                disabled={actionLoading === deleteTarget.id}
              >
                {t('cancelBtn')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrganizerEventGalleryAdminPage;
